// pages/Dashboard/Dashboard.tsx - Updated with tiles layout
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Home, 
  List, 
  FileText, 
  Copy, 
  Upload, 
  Eye, 
  TrendingUp, 
  AlertTriangle,
  CheckCircle,
  Clock,
  Users,
  BarChart3,
  Download,
  Settings,
  Search,
  Calendar,
  FileIcon,
  ExternalLink,
  MoreHorizontal,
  ChevronDown,
  Plus,
  FolderOpen,
  BarChart,
  Send
} from 'lucide-react';
import { ROUTES } from '../../constants/routes';
import './Dashboard.css';

interface Document {
  _id: string;
  filename: string;
  document_type: string;
  upload_date: string;
  processed_date: string;
  status: string;
  page_count?: number;
}

interface DashboardStats {
  totalDocuments: number;
  processedDocuments: number;
  pendingDocuments: number;
  highRiskTerms: number;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [stats, setStats] = useState<DashboardStats>({
    totalDocuments: 0,
    processedDocuments: 0,
    pendingDocuments: 0,
    highRiskTerms: 0
  });

  const quickActions = [
    {
      label: 'Upload New Contract',
      icon: Upload,
      action: () => {
        navigate(ROUTES.UPLOAD);
        setIsDropdownOpen(false);
      },
      primary: true
    },
    {
      label: 'View All Documents',
      icon: FolderOpen,
      action: () => {
        navigate(ROUTES.DOCUMENTS);
        setIsDropdownOpen(false);
      }
    },
    {
      label: 'Export Reports',
      icon: Download,
      action: () => {
        console.log('Export reports clicked');
        setIsDropdownOpen(false);
      }
    },
    {
      label: 'Share Insights',
      icon: Send,
      action: () => {
        console.log('Share insights clicked');
        setIsDropdownOpen(false);
      }
    },
    {
      label: 'Analytics Dashboard',
      icon: BarChart,
      action: () => {
        console.log('Analytics dashboard clicked');
        setIsDropdownOpen(false);
      }
    }
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('📊 Fetching dashboard data...');
      
      // Fetch documents
      const response = await fetch('http://localhost:8000/api/documents');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      const docs = data.data || [];
      
      console.log('📄 Dashboard loaded:', docs.length, 'documents');
      setDocuments(docs);
      
      // Calculate stats
      const totalDocuments = docs.length;
      const processedDocuments = docs.filter((doc: Document) => 
        doc.status.toLowerCase().includes('processed') || 
        doc.status.toLowerCase().includes('completed')
      ).length;
      const pendingDocuments = docs.filter((doc: Document) => 
        doc.status.toLowerCase().includes('processing') || 
        doc.status.toLowerCase().includes('pending')
      ).length;
      
      setStats({
        totalDocuments,
        processedDocuments,
        pendingDocuments,
        highRiskTerms: Math.floor(totalDocuments * 0.15) // Estimated high risk terms
      });
      
    } catch (err) {
      console.error('❌ Dashboard error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentClick = (document: Document) => {
    console.log('🎯 Navigating to document:', document._id);
    navigate(`/analysis/${document._id}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusInfo = (status: string) => {
    switch (status.toLowerCase()) {
      case 'enhanced_processed_v3_highlighting':
      case 'enhanced_processed_v2':
      case 'completed':
        return { label: 'Completed', className: 'completed' };
      case 'processing':
        return { label: 'Processing', className: 'processing' };
      case 'failed':
        return { label: 'Failed', className: 'failed' };
      default:
        return { label: 'Pending', className: 'processing' };
    }
  };

  const getDocumentIcon = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf':
        return 'PDF';
      case 'doc':
      case 'docx':
        return 'DOC';
      case 'txt':
        return 'TXT';
      case 'jpg':
      case 'jpeg':
      case 'png':
        return 'IMG';
      default:
        return filename.substring(0, 2).toUpperCase();
    }
  };

  const recentDocuments = documents
    .sort((a, b) => new Date(b.processed_date || b.upload_date).getTime() - new Date(a.processed_date || a.upload_date).getTime())
    .slice(0, 8); // Show more documents in tile view

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-300 text-lg">Loading dashboard...</p>
            <p className="text-gray-500 text-sm mt-2">Fetching your documents and analytics</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="min-h-screen flex items-center justify-center">
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-8 max-w-md text-center">
            <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">Error Loading Dashboard</h3>
            <p className="text-gray-400 mb-6">{error}</p>
            <button 
              onClick={fetchDashboardData}
              className="btn btn-primary"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Top Navigation Bar */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Left Navigation */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <button className="nav-icon bg-blue-600 text-white" title="Dashboard">
                  <Home size={20} />
                </button>
                <button 
                  onClick={() => navigate(ROUTES.DOCUMENTS)}
                  className="nav-icon"
                  title="Documents List"
                >
                  <List size={20} />
                </button>
                <button 
                  onClick={() => navigate(ROUTES.UPLOAD)}
                  className="nav-icon"
                  title="Upload Document"
                >
                  <FileText size={20} />
                </button>
                <button className="nav-icon" title="Notes">
                  <Copy size={20} />
                </button>
              </div>
            </div>

            {/* Center Title */}
            <div className="text-center">
              <h1 className="text-2xl font-bold text-white">Artifi Contract Analyzer</h1>
              <p className="text-sm text-gray-400">powered by <span className="font-semibold">Artifi Agentic Studio</span></p>
            </div>

            {/* Right Actions */}
            <div className="flex items-center space-x-3">
              <button className="nav-icon" title="Search">
                <Search size={20} />
              </button>
              <button className="nav-icon" title="Settings">
                <Settings size={20} />
              </button>
              
              {/* Quick Actions Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="btn btn-primary flex items-center gap-2"
                >
                  <Plus size={16} />
                  Quick Actions
                  <ChevronDown size={16} className={`transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isDropdownOpen && (
                  <div className="absolute right-0 top-full mt-2 w-64 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50">
                    <div className="py-2">
                      {quickActions.map((action, index) => (
                        <button
                          key={index}
                          onClick={action.action}
                          className={`w-full px-4 py-3 text-left hover:bg-gray-700 flex items-center gap-3 transition-colors ${
                            action.primary ? 'text-blue-400 border-b border-gray-700' : 'text-gray-300'
                          }`}
                        >
                          <action.icon size={18} />
                          <span className="font-medium">{action.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Header */}
      <div className="dashboard-header">
        <h1>Welcome to Your Contract Dashboard</h1>
        <p>Monitor your contract analysis progress and insights in real-time</p>
      </div>

      {/* Dashboard Content */}
      <div className="dashboard-content">
        {/* Stats Cards - Keep unchanged */}
        <div className="dashboard-grid dashboard-grid-4">
          {/* Total Documents */}
          <div className="stats-card">
            <div className="stats-card-content">
              <div className="stats-info">
                <h3>Total Documents</h3>
                <div className="stat-number">{stats.totalDocuments}</div>
                <div className="stat-change positive">
                  <TrendingUp size={16} />
                  <span>+{Math.floor(stats.totalDocuments * 0.1)} this month</span>
                </div>
              </div>
              <div className="icon-container blue">
                <FileText className="text-white" size={24} />
              </div>
            </div>
          </div>

          {/* Processed Documents */}
          <div className="stats-card">
            <div className="stats-card-content">
              <div className="stats-info">
                <h3>Processed</h3>
                <div className="stat-number">{stats.processedDocuments}</div>
                <div className="stat-change positive">
                  <CheckCircle size={16} />
                  <span>{stats.totalDocuments > 0 ? Math.round((stats.processedDocuments / stats.totalDocuments) * 100) : 0}% complete</span>
                </div>
              </div>
              <div className="icon-container green">
                <CheckCircle className="text-white" size={24} />
              </div>
            </div>
          </div>

          {/* Pending Analysis */}
          <div className="stats-card">
            <div className="stats-card-content">
              <div className="stats-info">
                <h3>Pending Analysis</h3>
                <div className="stat-number">{stats.pendingDocuments}</div>
                <div className="stat-change neutral">
                  <Clock size={16} />
                  <span>In queue</span>
                </div>
              </div>
              <div className="icon-container yellow">
                <Clock className="text-white" size={24} />
              </div>
            </div>
          </div>

          {/* High Risk Terms */}
          <div className="stats-card">
            <div className="stats-card-content">
              <div className="stats-info">
                <h3>High Risk Terms</h3>
                <div className="stat-number">{stats.highRiskTerms}</div>
                <div className="stat-change negative">
                  <AlertTriangle size={16} />
                  <span>Requires attention</span>
                </div>
              </div>
              <div className="icon-container purple">
                <AlertTriangle className="text-white" size={24} />
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="dashboard-main-grid">
          {/* Recent Documents - Tiles Layout */}
          <div className="recent-documents">
            <div className="recent-documents-header">
              <h3>Recent Documents</h3>
              <p>Your latest contract analysis results</p>
            </div>
            <div className="documents-list">
              {recentDocuments.length === 0 ? (
                <div className="documents-empty-state">
                  <FileText className="empty-icon" />
                  <h4>No documents yet</h4>
                  <p>Upload your first contract to get started</p>
                  <button
                    onClick={() => quickActions[0].action()}
                    className="btn btn-primary"
                  >
                    <Upload size={16} />
                    Upload Document
                  </button>
                </div>
              ) : (
                <div className="documents-grid">
                  {recentDocuments.map((document) => {
                    const statusInfo = getStatusInfo(document.status);
                    return (
                      <div
                        key={document._id}
                        className="document-tile"
                        onClick={() => handleDocumentClick(document)}
                      >
                        <div className="document-tile-header">
                          <div className="document-icon">
                            {getDocumentIcon(document.filename)}
                          </div>
                          <div className="document-tile-content">
                            <h4>{document.filename}</h4>
                            <div className="document-tile-meta">
                              <div className="document-type">{document.document_type}</div>
                              <div className="document-date">
                                <Calendar size={12} />
                                {formatDate(document.processed_date || document.upload_date)}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="document-status">
                          <div className={`status-dot ${statusInfo.className}`}></div>
                          {statusInfo.label}
                        </div>

                        <div className="document-tile-footer">
                          <div className="document-pages">
                            <FileIcon size={12} />
                            {document.page_count ? `${document.page_count} pages` : 'Unknown pages'}
                          </div>
                          <div className="document-actions">
                            <button 
                              className="action-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDocumentClick(document);
                              }}
                              title="View Analysis"
                            >
                              <Eye size={12} />
                            </button>
                            <button 
                              className="action-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                // Handle external link
                                console.log('External link for:', document._id);
                              }}
                              title="Open"
                            >
                              <ExternalLink size={12} />
                            </button>
                            <button 
                              className="action-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                // Handle more options
                                console.log('More options for:', document._id);
                              }}
                              title="More Options"
                            >
                              <MoreHorizontal size={12} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {recentDocuments.length > 0 && (
              <div className="p-4 border-t border-gray-700">
                <button
                  onClick={() => navigate(ROUTES.DOCUMENTS)}
                  className="btn btn-outline w-full"
                >
                  <List size={16} />
                  View All Documents
                </button>
              </div>
            )}
          </div>

          {/* Sidebar - Risk Analysis and System Status Only */}
          <div className="space-y-6">
            {/* Risk Analysis Overview */}
            <div className="risk-analysis">
              <h3>Risk Distribution</h3>
              <div className="space-y-4">
                <div className="risk-item">
                  <div className="risk-header">
                    <span className="risk-label">High Risk</span>
                    <span className="risk-percentage">23%</span>
                  </div>
                  <div className="risk-bar">
                    <div className="risk-progress high" style={{ width: '23%' }}></div>
                  </div>
                </div>
                <div className="risk-item">
                  <div className="risk-header">
                    <span className="risk-label">Medium Risk</span>
                    <span className="risk-percentage">45%</span>
                  </div>
                  <div className="risk-bar">
                    <div className="risk-progress medium" style={{ width: '45%' }}></div>
                  </div>
                </div>
                <div className="risk-item">
                  <div className="risk-header">
                    <span className="risk-label">Low Risk</span>
                    <span className="risk-percentage">32%</span>
                  </div>
                  <div className="risk-bar">
                    <div className="risk-progress low" style={{ width: '32%' }}></div>
                  </div>
                </div>
              </div>
            </div>

            {/* System Status */}
            <div className="quick-actions">
              <h3>System Status</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full status-indicator"></div>
                    <span className="text-sm text-gray-300">API Service</span>
                  </div>
                  <span className="text-xs text-green-400">Online</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full status-indicator"></div>
                    <span className="text-sm text-gray-300">Database</span>
                  </div>
                  <span className="text-xs text-green-400">Connected</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-yellow-400 rounded-full status-indicator"></div>
                    <span className="text-sm text-gray-300">Processing Queue</span>
                  </div>
                  <span className="text-xs text-yellow-400">{stats.pendingDocuments} pending</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <Users size={16} className="text-gray-400" />
                <span className="text-sm text-gray-400">Powered by Artifi Agentic Studio</span>
              </div>
              <div className="flex items-center space-x-2">
                <BarChart3 size={16} className="text-gray-400" />
                <span className="text-sm text-gray-400">Advanced AI Contract Analysis</span>
              </div>
            </div>
            <div className="text-sm text-gray-500">
              Last updated: {new Date().toLocaleDateString()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;